import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Upload, X } from 'lucide-react';

/**
 * An import is about to overwrite something the author has already written.
 *
 * The two boxes in the Custom HTML/CSS editor are often the only copy of a
 * layout — it lives in the deck, not on disk — so replacing one without asking
 * would be a way to lose an afternoon by picking the wrong file. Asking only
 * happens when there is something to lose: filling an empty box destroys
 * nothing and goes straight through.
 *
 * One dialog covers both boxes rather than two in a row, because an import can
 * carry both halves and answering the same question twice reads as a bug.
 *
 * @param {object} props
 * @param {Array<string>} props.replacing - box names, e.g. ['HTML', 'CSS']
 * @param {object} [props.sources] - `{ html, css }` file names, when known
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
export default function ConfirmReplaceDialog({ replacing = [], sources = {}, onConfirm, onCancel }) {
  const cancelRef = useRef(null);

  // Focus starts on Cancel, the opposite of the save dialogs: this one is
  // reached by picking a file rather than by asking for the action, so the
  // safe answer should be the one a stray Enter gives.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onCancel]);

  if (!replacing.length) return null;

  const list = replacing.join(' and ');
  const named = [
    replacing.includes('HTML') && sources.html ? `HTML from ${sources.html}` : '',
    replacing.includes('CSS') && sources.css ? `CSS from ${sources.css}` : ''
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-replace-title"
      aria-describedby="confirm-replace-body"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start gap-4 rounded-t-2xl border-b border-gray-200 bg-amber-50 px-6 py-5">
          <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle size={20} />
          </div>
          <h2 id="confirm-replace-title" className="min-w-0 flex-1 text-xl font-semibold text-gray-900">
            Replace existing {list}?
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg p-2 text-gray-500 hover:bg-amber-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div id="confirm-replace-body" className="space-y-3 px-6 py-5 text-sm leading-relaxed text-gray-700">
          <p>
            Importing will replace the {list} currently in the editor.
          </p>
          {named.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-gray-600">
              {named.map((line) => <li key={line}>{line}</li>)}
            </ul>
          )}
          <p className="text-gray-500">
            Nothing is saved to the slide until you press Save, so cancelling here leaves the
            editor exactly as it is.
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="order-last rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 sm:order-first sm:mr-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
          >
            <Upload size={16} />
            Replace
          </button>
        </div>
      </div>
    </div>
  );
}
